import { Search, PenTool, Rocket, TrendingUp } from "lucide-react";

const steps = [
    {
        number: "01",
        icon: Search,
        title: "Discover",
        description: "We understand your goals, challenges, and technology landscape.",
    },
    {
        number: "02",
        icon: PenTool,
        title: "Design",
        description: "We architect scalable, secure, and future-ready solutions.",
    },
    {
        number: "03",
        icon: Rocket,
        title: "Deliver",
        description: "We implement with precision, agility, and minimal disruption.",
    },
    {
        number: "04",
        icon: TrendingUp,
        title: "Optimize",
        description: "We continuously improve performance, security, and efficiency.",
    },
];

export default function OurProcess() {
    return (
        <section className="relative py-16 md:py-24 overflow-hidden">
            <style>{`
                @keyframes drawLine {
                    from { width: 0%; }
                    to   { width: 100%; }
                }
                .timeline-line {
                    animation: drawLine 1.2s cubic-bezier(0.4,0,0.2,1) 0.3s both;
                }
                .step-card {
                    transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
                }
                .step-card:hover {
                    transform: translateY(-6px);
                    border-color: rgba(147,51,234,0.4) !important;
                    box-shadow: 0 0 36px rgba(147,51,234,0.15);
                }
                .step-card:hover .step-icon {
                    box-shadow: 0 6px 28px rgba(147,51,234,0.55);
                    transform: scale(1.08);
                }
                .step-icon {
                    transition: box-shadow 0.3s ease, transform 0.3s ease;
                }
            `}</style>

            {/* Ambient glows */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute top-[20%] left-[10%] w-[350px] h-[280px] rounded-full blur-[120px]"
                    style={{ background: "rgba(147,51,234,0.07)" }} />
                <div className="absolute bottom-[20%] right-[10%] w-[300px] h-[240px] rounded-full blur-[120px]"
                    style={{ background: "rgba(59,130,246,0.07)" }} />
            </div>

            <div className="container relative z-10">

                {/* Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-[2.6rem] font-bold text-white leading-snug mb-4">
                        Our Process
                        {/* <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent pl-2"> */}
                           
                        {/* </span> */}
                    </h2>
                    {/* <div className="w-12 h-0.5 rounded-full bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 mx-auto" /> */}
                </div>

                {/* Timeline — desktop */}
                <div className="hidden md:block">

                    {/* Connector line */}
                    <div className="relative flex items-center justify-between mb-0 px-16">
                        {steps.map((step, i) => (
                            <div key={step.number} className="relative flex flex-col items-center flex-1">
                                {/* Dot */}
                                <div
                                    className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600 step-icon"
                                    style={{ boxShadow: "0 4px 20px rgba(147,51,234,0.4)" }}
                                >
                                    {(() => { const Icon = step.icon; return <Icon className="w-6 h-6 text-white" />; })()}
                                </div>

                                {/* Connecting line between dots */}
                                {i < steps.length - 1 && (
                                    <div className="absolute top-7 left-1/2 w-full h-[2px] z-0"
                                        style={{ background: "rgba(255,255,255,0.06)" }}
                                    >
                                        <div
                                            className="timeline-line h-full rounded-full"
                                            style={{
                                                background: "linear-gradient(90deg, #a855f7, #3b82f6)",
                                                animationDelay: `${i * 0.2}s`,
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Cards below dots */}
                    <div className="grid grid-cols-4 gap-5 mt-8">
                        {steps.map((step) => {
                            return (
                                <div
                                    key={step.number}
                                    className="step-card rounded-2xl border p-6 text-center"
                                    style={{
                                        background: "linear-gradient(135deg, rgba(147,51,234,0.06) 0%, rgba(37,99,235,0.06) 100%)",
                                        borderColor: "rgba(255,255,255,0.08)",
                                    }}
                                >
                                    <p
                                        className="text-4xl font-black mb-3 select-none"
                                        style={{
                                            background: "linear-gradient(135deg, rgba(147,51,234,0.3), rgba(59,130,246,0.3))",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                        }}
                                    >
                                        {step.number}
                                    </p>
                                    <h4 className="text-white font-bold text-lg mb-2">{step.title}</h4>
                                    <div className="w-8 h-0.5 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 mx-auto mb-3" />
                                    <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
                                        {step.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Timeline — mobile (vertical) */}
                <div className="md:hidden flex flex-col gap-0">
                    {steps.map((step, i) => {
                        const Icon = step.icon;
                        return (
                            <div key={step.number} className="flex gap-5">

                                {/* Left — dot + line */}
                                <div className="flex flex-col items-center">
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-r from-purple-600 to-blue-600"
                                        style={{ boxShadow: "0 4px 16px rgba(147,51,234,0.4)" }}
                                    >
                                        <Icon className="w-5 h-5 text-white" />
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div className="w-[2px] flex-1 my-2 rounded-full"
                                            style={{ background: "linear-gradient(to bottom, #a855f7, #3b82f6)" }} />
                                    )}
                                </div>

                                {/* Right — content */}
                                <div className="pb-8 flex-1">
                                    <p
                                        className="text-2xl font-black mb-1 select-none"
                                        style={{
                                            background: "linear-gradient(135deg, rgba(147,51,234,0.4), rgba(59,130,246,0.4))",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                        }}
                                    >
                                        {step.number}
                                    </p>
                                    <h4 className="text-white font-bold text-lg mb-1">{step.title}</h4>
                                    <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
                                        {step.description}
                                    </p>
                                </div>

                            </div>
                        );
                    })}
                </div>

            </div>
        </section>
    );
}